// Seção "Journeys Zenvia" da tela de Provedores — tabela evento
// (label pt-BR) · canal · journeyId (mono, copiável) · versionId ·
// ações editar/remover (AlertDialog) + botão de adicionar. Acima da
// tabela, ALERTA destacado (tom warning) com os eventos ATIVOS sem
// journey mapeada — cada chip abre o dialog de adicionar
// pré-preenchido com evento+canal.
import {
  PencilIcon,
  PlusIcon,
  RouteIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { SectionCard } from "@/components/shared/detail-components"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type {
  NotificationZenviaConfigType,
  NotificationZenviaJourneyType,
} from "@/types/notification-center"

import { useDeleteZenviaJourney } from "../provider-mutations"
import {
  ZenviaJourneyFormDialog,
  type ZenviaJourneyPrefill,
} from "./zenvia-journey-form-dialog"

const journeysPrefix =
  "pages.notificationCenter.providersPage.journeys"

// Estado do dialog de form: fechado, criação (com/sem prefill) ou
// edição de uma journey existente.
type JourneyDialogState =
  | { mode: "closed" }
  | { mode: "create"; prefill?: ZenviaJourneyPrefill }
  | { mode: "edit"; journey: NotificationZenviaJourneyType }

type ZenviaJourneysSectionProps = {
  whiteLabelId: string
  zenvia: NotificationZenviaConfigType
}

export const ZenviaJourneysSection = ({
  whiteLabelId,
  zenvia,
}: ZenviaJourneysSectionProps) => {
  const { t } = useTranslation()
  const [dialogState, setDialogState] =
    useState<JourneyDialogState>({ mode: "closed" })
  const [journeyToDelete, setJourneyToDelete] =
    useState<NotificationZenviaJourneyType | null>(null)
  const deleteJourney = useDeleteZenviaJourney(whiteLabelId)

  const { journeys, unmappedActiveEvents } = zenvia

  return (
    <SectionCard
      title={t(`${journeysPrefix}.title`)}
      icon={
        <RouteIcon className="size-4" aria-hidden="true" />
      }
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setDialogState({ mode: "create" })}
        >
          <PlusIcon
            className="mr-1.5 size-4"
            aria-hidden="true"
          />
          {t(`${journeysPrefix}.add`)}
        </Button>
      }
    >
      <div className="space-y-4 p-4">
        {/* Alerta warning: eventos ATIVOS sem journey mapeada. */}
        {unmappedActiveEvents.length > 0 && (
          <div
            data-testid="zenvia-unmapped-alert"
            role="alert"
            className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4"
          >
            <div className="flex items-start gap-2">
              <TriangleAlertIcon
                className="mt-0.5 size-4 shrink-0 text-amber-600"
                aria-hidden="true"
              />
              <p className="text-sm font-medium text-amber-800">
                {unmappedActiveEvents.length === 1
                  ? t(`${journeysPrefix}.unmappedTitleOne`)
                  : t(
                      `${journeysPrefix}.unmappedTitleMany`,
                      {
                        count: unmappedActiveEvents.length,
                      }
                    )}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {unmappedActiveEvents.map((unmapped) => (
                <button
                  key={`${unmapped.event}-${unmapped.channel}`}
                  type="button"
                  className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                  aria-label={t(
                    `${journeysPrefix}.mapUnmapped`,
                    { event: unmapped.eventLabel }
                  )}
                  onClick={() =>
                    setDialogState({
                      mode: "create",
                      prefill: {
                        event: unmapped.event,
                        channel: unmapped.channel,
                      },
                    })
                  }
                >
                  {unmapped.eventLabel} · {unmapped.channel}
                </button>
              ))}
            </div>
          </div>
        )}

        {journeys.length === 0 ? (
          <p
            data-testid="zenvia-journeys-empty"
            className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground"
          >
            {t(`${journeysPrefix}.empty`)}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t(`${journeysPrefix}.columns.event`)}
                  </TableHead>
                  <TableHead>
                    {t(`${journeysPrefix}.columns.channel`)}
                  </TableHead>
                  <TableHead>
                    {t(
                      `${journeysPrefix}.columns.journeyId`
                    )}
                  </TableHead>
                  <TableHead>
                    {t(
                      `${journeysPrefix}.columns.versionId`
                    )}
                  </TableHead>
                  <TableHead className="w-24 text-right">
                    {t(`${journeysPrefix}.columns.actions`)}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {journeys.map((journey) => (
                  <TableRow key={journey.id}>
                    <TableCell className="font-medium">
                      {journey.eventLabel}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="rounded-full font-medium"
                      >
                        {journey.channel}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 font-mono text-xs">
                        {journey.journeyId}
                        <ClipboardButton
                          text={journey.journeyId}
                        />
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs tabular-nums">
                      {journey.versionId ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          aria-label={t(
                            `${journeysPrefix}.edit`,
                            { event: journey.eventLabel }
                          )}
                          onClick={() =>
                            setDialogState({
                              mode: "edit",
                              journey,
                            })
                          }
                        >
                          <PencilIcon
                            className="size-4"
                            aria-hidden="true"
                          />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7 text-red-600 hover:text-red-700"
                          aria-label={t(
                            `${journeysPrefix}.remove`,
                            { event: journey.eventLabel }
                          )}
                          onClick={() =>
                            setJourneyToDelete(journey)
                          }
                        >
                          <Trash2Icon
                            className="size-4"
                            aria-hidden="true"
                          />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Dialog de criação/edição (write otimista fecha no mutate). */}
      {dialogState.mode !== "closed" && (
        <ZenviaJourneyFormDialog
          whiteLabelId={whiteLabelId}
          journey={
            dialogState.mode === "edit"
              ? dialogState.journey
              : undefined
          }
          prefill={
            dialogState.mode === "create"
              ? dialogState.prefill
              : undefined
          }
          onClose={() => setDialogState({ mode: "closed" })}
        />
      )}

      {/* Confirmação de remoção (write destrutivo). */}
      <AlertDialog
        open={Boolean(journeyToDelete)}
        onOpenChange={(open) =>
          !open && setJourneyToDelete(null)
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${journeysPrefix}.deleteDialog.title`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${journeysPrefix}.deleteDialog.description`,
                { event: journeyToDelete?.eventLabel }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${journeysPrefix}.deleteDialog.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (journeyToDelete) {
                  deleteJourney.mutate({
                    journeyRecordId: journeyToDelete.id,
                  })
                }
                setJourneyToDelete(null)
              }}
            >
              {t(`${journeysPrefix}.deleteDialog.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  )
}
